import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';

import { ButtonComponent } from '../../../common/button/button.component';
import { Invoice, InvoiceCollection, InvoiceDetailInputData, InvoiceActionResult } from '../../../../model/subscription-billing';

// Re-export for convenience
export { InvoiceDetailInputData, InvoiceActionResult } from '../../../../model/subscription-billing';

@Component({
  selector: 'app-invoice-detail',
  imports: [ButtonComponent],
  templateUrl: './invoice-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './invoice-detail.component.scss'
})
export class InvoiceDetailComponent {
  readonly inputData = input<InvoiceDetailInputData | null>(null);
  readonly actionPerformed = output<InvoiceActionResult>();
  readonly closed = output<void>();

  get invoice(): Invoice | null {
    return this.inputData()?.invoice ?? null;
  }

  // --- Collection status block (gap A): why this invoice is/isn't being collected ---

  get collection(): InvoiceCollection | null {
    return this.invoice?.collection ?? null;
  }

  get showCollectionBlock(): boolean {
    const s = this.invoice?.status;
    return !!this.collection?.blockedBy && (s === 'OPEN' || s === 'PARTIALLY_PAID');
  }

  get collectionMessage(): string {
    const c = this.collection;
    if (!c?.blockedBy) return '';
    const reason = c.lastFailureMessage ? ` (${c.lastFailureMessage})` : '';
    switch (c.blockedBy) {
      case 'NO_INSTRUMENT':
        return 'Not being collected: auto-pay is on but the client has no payment card. '
          + 'Add a card and it will charge on the next billing run.';
      case 'MANUAL_PAY':
        return 'Client pays manually. Use Charge Card or Record Payment.';
      case 'RETRY_SCHEDULED':
        return `Charge declined${reason}. Attempt ${c.totalAttempts} of ${c.maxAttempts}; `
          + `retry scheduled for ${c.nextRetryAt ? this.formatDateTime(c.nextRetryAt) : 'the next run'}.`;
      case 'EXHAUSTED':
        return `Automatic retries exhausted${reason}. Client is past due. `
          + 'Add a new card to resume collection.';
      case 'PENDING':
        return 'Collection re-armed; will charge on the next billing run.';
    }
  }

  get showGoToProfileLink(): boolean {
    const b = this.collection?.blockedBy;
    return b === 'NO_INSTRUMENT' || b === 'EXHAUSTED';
  }

  formatDateTime(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    }).format(new Date(date));
  }

  handleGoToPaymentProfile(): void {
    if (this.invoice) {
      this.actionPerformed.emit({ action: 'go-to-payment-profile', invoice: this.invoice });
    }
  }

  formatCurrency(amount: number): string {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
    return amount < 0 ? `-${formatted}` : formatted;
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  }

  getStatusClass(status: string): string {
    const statusClasses: Record<string, string> = {
      'PAID': 'status-active',
      'OPEN': 'status-warning',
      'VOID': 'status-neutral',
      'DRAFT': 'status-neutral',
      'UNCOLLECTIBLE': 'status-error',
      'PARTIALLY_PAID': 'status-warning'
    };
    return statusClasses[status] || 'status-neutral';
  }

  getInvoiceTypeDisplay(type: string): string {
    const types: Record<string, string> = {
      'SUBSCRIPTION': 'Subscription',
      'ONE_TIME': 'One-Time',
      'CREDIT_NOTE': 'Credit Note'
    };
    return types[type] || type;
  }

  getInvoiceTypeClass(type: string): string {
    const classes: Record<string, string> = {
      'SUBSCRIPTION': 'type-subscription',
      'ONE_TIME': 'type-onetime',
      'CREDIT_NOTE': 'type-credit'
    };
    return classes[type] || '';
  }

  getPaymentMethodDisplay(method: string): string {
    const methods: Record<string, string> = {
      'CARD': 'Credit Card',
      'CREDIT_BALANCE': 'Account Credit',
      'BANK_TRANSFER': 'Bank Transfer'
    };
    return methods[method] || method;
  }

  handleRecordPayment(): void {
    if (this.invoice) {
      this.actionPerformed.emit({ action: 'record-payment', invoice: this.invoice });
    }
  }

  handleChargeCard(): void {
    if (this.invoice) {
      this.actionPerformed.emit({ action: 'charge-card', invoice: this.invoice });
    }
  }

  handleVoid(): void {
    if (this.invoice) {
      this.actionPerformed.emit({ action: 'void', invoice: this.invoice });
    }
  }

  /**
   * Write-off. moneta allows it on an ACTIVE invoice with a balance left (`Invoice.writeOff`),
   * which is OPEN or PARTIALLY_PAID with something still due — anything else it rejects.
   */
  get canMarkUncollectible(): boolean {
    const invoice = this.invoice;
    if (!invoice) return false;
    return (invoice.status === 'OPEN' || invoice.status === 'PARTIALLY_PAID') && invoice.amountDue > 0;
  }

  /**
   * A credit note corrects an invoice money already moved on: moneta requires the original to
   * be ACTIVE with payments against it, and refuses a credit note against a credit note. An
   * unpaid invoice is voided instead, which is the button next to this one.
   */
  get canCreditNote(): boolean {
    const invoice = this.invoice;
    if (!invoice) return false;
    if (invoice.type === 'CREDIT_NOTE') return false;
    const active = invoice.status === 'OPEN' || invoice.status === 'PARTIALLY_PAID' || invoice.status === 'PAID';
    return active && invoice.amountPaid > 0;
  }

  handleMarkUncollectible(): void {
    if (this.invoice) {
      this.actionPerformed.emit({ action: 'mark-uncollectible', invoice: this.invoice });
    }
  }

  handleCreditNote(): void {
    if (this.invoice) {
      this.actionPerformed.emit({ action: 'credit-note', invoice: this.invoice });
    }
  }

  handleClose(): void {
    this.closed.emit();
  }
}